import { HttpErrorResponse, type HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, throwError } from 'rxjs';

import { isApiUrl } from '@core/api/api-url';
import { APP_CONFIG } from '@core/app-config';
import { ApiError, type ErrorParams, type FieldError, type FieldErrors } from '@core/api/api-error';

export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  if (!isApiUrl(inject(APP_CONFIG).apiBaseUrl, req.url)) {
    return next(req);
  }

  return next(req).pipe(
    catchError((error: unknown) =>
      throwError(() => (error instanceof HttpErrorResponse ? toApiError(error) : error)),
    ),
  );
};

interface ErrorBody {
  readonly code?: string;
  readonly detail?: string;
  readonly traceId?: string;
  readonly details?: readonly string[];
  readonly errors?: FieldErrors;
  readonly params?: ErrorParams;
}

function toApiError(error: HttpErrorResponse): ApiError {
  if (error.status === 0) {
    return new ApiError(0, 'Unable to reach the server.');
  }
  const body = readErrorBody(error.error);
  return new ApiError(error.status, body.detail ?? error.message, {
    code: body.code,
    traceId: body.traceId,
    details: body.details,
    retryAfterSeconds: readRetryAfter(error.headers.get('Retry-After')),
    fieldErrors: body.errors,
    params: body.params,
  });
}

// Every API failure has the shape { code, detail, traceId, details }; a validation failure
// adds `errors`, a map of field name to the rules it broke, and some errors add `params`,
// the values their message needs.
function readErrorBody(body: unknown): ErrorBody {
  if (typeof body !== 'object' || body === null) {
    return {};
  }
  const record = body as Record<string, unknown>;
  const text = (key: string) => (typeof record[key] === 'string' ? record[key] : undefined);
  const details = Array.isArray(record['details'])
    ? record['details'].filter((item): item is string => typeof item === 'string')
    : undefined;
  return {
    code: text('code'),
    detail: text('detail'),
    traceId: text('traceId'),
    details,
    errors: readFieldErrors(record['errors']),
    params: readParams(record['params']),
  };
}

/** Keeps only string values and lists of strings; anything else is dropped. */
function readParams(value: unknown): ErrorParams | undefined {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return undefined;
  }
  const params: Record<string, string | readonly string[]> = {};
  for (const [key, entry] of Object.entries(value)) {
    if (typeof entry === 'string') {
      params[key] = entry;
    } else if (Array.isArray(entry)) {
      params[key] = entry.filter((item): item is string => typeof item === 'string');
    }
  }
  return params;
}

function readFieldErrors(value: unknown): FieldErrors | undefined {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return undefined;
  }
  const errors: Record<string, FieldError[]> = {};
  for (const [field, entries] of Object.entries(value)) {
    if (!Array.isArray(entries)) {
      continue;
    }
    errors[field] = entries.flatMap((entry: unknown): FieldError[] => {
      const item = entry as Record<string, unknown> | null;
      if (typeof item?.['category'] !== 'string') {
        return [];
      }
      const text = typeof item['text'] === 'string' ? item['text'] : undefined;
      return [{ category: item['category'], ...(text === undefined ? {} : { text }) }];
    });
  }
  return errors;
}

function readRetryAfter(value: string | null): number | null {
  const seconds = Number(value);
  return value !== null && Number.isInteger(seconds) && seconds > 0 ? seconds : null;
}
